// * import third party
import { useEffect, useState } from 'react';
import {
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Box,
    Flex,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon, CloseIcon } from '@chakra-ui/icons';

// * import local
import AddItemButton from '../ui/AddItemButton';
import { CheckMarkIcon } from '@assets/icons';
import { useBundleStore } from '@pages/UploadMappingV2/hooks/state/useAddProductBundle';
import { useGetInfiniteListMasterProduct } from '@pages/UploadMappingV2/hooks/query/useGetListMasterProduct';
import InfiniteScrollObserver from '../InfiniteScrollObserver';
import { IterationComponent } from '@components/functionComponent';
import SkeletonComponent from '@components/skeleton';
import { useDebounce } from '@hooks/useDebounce';
import EmptyState from '../SearchProduct/EmptyState';

export default function TableAddProduct({ tableHeadLists = [], tableData = [] }) {
    if (!tableHeadLists || !tableData) return <Text>There was an error displaying your data!</Text>;

    const [value, setValue] = useState(null);
    const [selected, setSelected] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { bundle, errors, addItem, isEditingRow, setIsEditingRow, updateItem } = useBundleStore();
    const debouncedValue = useDebounce(value, 500);

    const {
        data: infiniteData,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isLoading: isLoadingInfiniteData,
    } = useGetInfiniteListMasterProduct({ search: debouncedValue });
    const showEmpty = !isLoadingInfiniteData && value?.length > 3 && !infiniteData?.length;

    const handleSearch = (e) => {
        setValue(e.target.value);
    };

    const handleSelect = (product) => () => {
        setSelected({ ...product, id: bundle?.items?.length + 1 });
        setValue(product.name);
    };

    const handleAddProduct = () => {
        if (!selected) return;
        if (isEditingRow) {
            updateItem(isEditingRow.id, { ...selected });
        } else {
            addItem(selected);
        }
        handleCloseItems();
    };

    const handleCloseItems = () => {
        if (isEditingRow) {
            setIsEditingRow(false);
        }

        onClose();
        setValue('');
        setSelected(null);
    };

    const tableHeadMapping = () => {
        return tableHeadLists.map((value) => (
            <Th
                textTransform={'none'}
                fontWeight={'bold'}
                color={'semantic.600'}
                fontFamily={'inherit'}
                fontSize={'14px'}
                p={'12px'}
                letterSpacing={'normal'}
                width={value.width ?? '200px'}
                textAlign={value.align ?? 'left'}
            >
                {value.title}
            </Th>
        ));
    };

    const tableBodyMapping = () => {
        return bundle.items.map((item) => {
            const isEdit = isEditingRow.id === item.id;

            return (
                <>
                    {isEdit ? (
                        <Tr>
                            <Td colSpan={tableHeadLists.length - 1} p={'12px'} verticalAlign={'baseline'}>
                                <InputGroup position={'relative'}>
                                    <Input
                                        placeholder={'Cari nama atau kode produk Paragon...'}
                                        value={value}
                                        onChange={handleSearch}
                                        isInvalid={errors?.items}
                                    />
                                    <InputRightElement mx={'12px'} onClick={() => {}}>
                                        <ChevronDownIcon fontSize={'24px'} />
                                    </InputRightElement>
                                    {value?.length > 3 && !selected && productLists()}
                                </InputGroup>

                                <Text fontSize={'10px'} mt={'4px'} color={'danger.500'}>
                                    {errors.items}
                                </Text>
                            </Td>
                            <Td p={'12px'} verticalAlign={'top'}>
                                <Flex gap={'12px'}>
                                    <IconButton maxW={'30px'} size={'sm'} onClick={handleAddProduct}>
                                        <Box>
                                            <CheckMarkIcon color="#fff" size={18} />
                                        </Box>
                                    </IconButton>
                                    <IconButton
                                        maxW={'30px'}
                                        size={'sm'}
                                        variant={'outline'}
                                        onClick={handleCloseItems}
                                    >
                                        <CloseIcon fontSize={'10px'} />
                                    </IconButton>
                                </Flex>
                            </Td>
                        </Tr>
                    ) : (
                        <Tr>
                            {tableHeadLists.map((list) => (
                                <Td p={'12px'} fontSize={'14px'} textAlign={list.align ?? 'left'}>
                                    {list.tableDataList({ value: item[list.key], allValue: item })}
                                </Td>
                            ))}
                        </Tr>
                    )}
                </>
            );
        });
    };

    const productLists = () => {
        const renderItem = (value) => (
            <Box
                key={value.id}
                p="12px"
                transition=".3s all ease"
                cursor="pointer"
                borderRadius="8px"
                _hover={{ bg: 'semantic.200' }}
                onClick={handleSelect(value)}
            >
                <Text mb="4px">{value.name}</Text>
                <Text color="neutral.500" fontSize="14px">
                    {value.barcode}
                </Text>
            </Box>
        );

        const renderSkeleton = () => (
            <IterationComponent
                arr={[1, 2, 3]}
                render={() => (
                    <Box bg="semantic.200" mb="8px" p="12px" borderRadius="8px">
                        <SkeletonComponent width="50%" height="20px" mb="4px" />
                        <SkeletonComponent width="25%" height="17px" />
                    </Box>
                )}
            />
        );

        const Section = ({ title, children }) => (
            <Box mb="12px">
                <Text color="neutral.600" textTransform="uppercase" mb="8px" fontSize="14px">
                    {title}
                </Text>
                {children}
            </Box>
        );

        const uniqueItems = Object.values(
            bundle.items.reduce((acc, item) => {
                acc[item.barcode] = item;
                return acc;
            }, {})
        );

        return (
            <Box
                p="22px"
                borderRadius="8px"
                bg="white"
                shadow="sm"
                position="absolute"
                h="fit-content"
                maxH="400px"
                overflowY={showEmpty ? 'hidden' : 'scroll'}
                w="full"
                top="70px"
                zIndex={999}
            >
                {bundle.items.length > 0 && <Section title="Produk Sebelumnya">{uniqueItems.map(renderItem)}</Section>}

                {isLoadingInfiniteData && renderSkeleton()}

                {showEmpty && <EmptyState />}

                {!showEmpty && infiniteData?.length > 0 && (
                    <Section title="Produk Lainnya">
                        {infiniteData?.map(renderItem)}

                        {hasNextPage && (
                            <>
                                <InfiniteScrollObserver
                                    hasNextPage={hasNextPage}
                                    fetchNextPage={fetchNextPage}
                                    disabled={isFetchingNextPage}
                                    rootMargin="900px"
                                />
                                {isFetchingNextPage && renderSkeleton()}
                            </>
                        )}
                    </Section>
                )}
            </Box>
        );
    };

    useEffect(() => {
        if (isEditingRow) setValue(isEditingRow.name);
    }, [isEditingRow]);

    return (
        <Table variant="simple" minW={'800px'}>
            <Thead>
                <Tr>{tableHeadMapping()}</Tr>
            </Thead>

            <Tbody>
                {tableBodyMapping()}

                {(isOpen || (errors.items && bundle?.items?.length < 2)) && (
                    <Tr>
                        <Td colSpan={tableHeadLists.length - 1} p={'12px'} verticalAlign={'baseline'}>
                            <InputGroup position={'relative'}>
                                <Input
                                    placeholder={'Cari nama atau kode produk Paragon...'}
                                    value={value}
                                    onChange={handleSearch}
                                    isInvalid={errors?.items && bundle?.items?.length < 2}
                                />
                                <InputRightElement mx={'12px'} onClick={() => {}}>
                                    <ChevronDownIcon fontSize={'24px'} />
                                </InputRightElement>
                                {value?.length > 3 && !selected && productLists()}
                            </InputGroup>
                            {errors.items && bundle?.items?.lengt < 2 && (
                                <Text fontSize={'10px'} mt={'4px'} color={'danger.500'}>
                                    {errors.items}
                                </Text>
                            )}
                        </Td>
                        <Td p={'12px'} verticalAlign={'top'}>
                            <Flex gap={'12px'}>
                                <IconButton maxW={'30px'} size={'sm'} onClick={handleAddProduct}>
                                    <Box>
                                        <CheckMarkIcon color="#fff" size={18} />
                                    </Box>
                                </IconButton>
                                <IconButton maxW={'30px'} size={'sm'} variant={'outline'} onClick={handleCloseItems}>
                                    <CloseIcon fontSize={'10px'} />
                                </IconButton>
                            </Flex>
                        </Td>
                    </Tr>
                )}

                {bundle?.items?.length < 10 && (
                    <Tr>
                        <Td colSpan={tableHeadLists.length} p={'12px'}>
                            <AddItemButton btnTitle="Tambah Produk" onClick={onOpen} />
                        </Td>
                    </Tr>
                )}
            </Tbody>
        </Table>
    );
}
